import { useCallback, useState, useEffect, useRef } from 'react'
import { debounce } from 'lodash'
import { RemoveBoxButton } from './RemoveBoxButton'
import type { BoxItemProps } from './types'
import { DEBOUNCE_DELAY, BOX_PLACEHOLDERS, CHAR_COUNT_THRESHOLD } from './constants'

export function BoxItem({ box, color, onContentChange, onRemove, canRemove }: BoxItemProps) {
  const [localContent, setLocalContent] = useState(box.content)

  // Track if user has unsaved edits - prevents server sync from wiping user typing
  const hasUnsavedEditsRef = useRef(false)
  // Track latest content for blur/unmount flush
  const latestContentRef = useRef(box.content)

  // Update local state if box prop changes (from parent)
  // CRITICAL: Don't overwrite if user has unsaved changes - this prevents the
  // "content disappears after typing" bug where server sync overwrites user input
  useEffect(() => {
    if (!hasUnsavedEditsRef.current) {
      setLocalContent(box.content)
      latestContentRef.current = box.content
    }
  }, [box.content])

  // Debounced save to parent - clears the unsaved flag when complete
  // FR-3.3: 500ms debounce with flush on blur/unmount
  const debouncedSave = useRef(
    debounce((id: string, content: string) => {
      onContentChange(id, content)
      // After save completes, clear the unsaved flag so future parent updates can sync
      hasUnsavedEditsRef.current = false
    }, DEBOUNCE_DELAY)
  ).current

  // FR-3.3: Flush pending saves on unmount to prevent data loss
  useEffect(() => {
    return () => {
      // Flush any pending debounced save before component unmounts
      if (hasUnsavedEditsRef.current) {
        debouncedSave.flush()
      }
      debouncedSave.cancel()
    }
  }, [debouncedSave])

  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newContent = e.target.value
      // Mark that we have unsaved edits - this prevents parent sync from overwriting
      hasUnsavedEditsRef.current = true
      setLocalContent(newContent)
      latestContentRef.current = newContent
      debouncedSave(box.id, newContent)
    },
    [box.id, debouncedSave]
  )

  // FR-3.3: Flush pending saves on blur to ensure content is persisted
  // before user navigates away from the textarea
  const handleBlur = useCallback(() => {
    if (hasUnsavedEditsRef.current) {
      debouncedSave.flush()
    }
  }, [debouncedSave])

  const handleRemove = useCallback(() => {
    onRemove(box.id)
  }, [box.id, onRemove])

  // Use position-specific placeholder if available, fall back to generic
  const placeholder = BOX_PLACEHOLDERS[box.order] ?? `Describe what happens in box ${box.order}...`

  // Show character count when content exceeds threshold (FR-3.4)
  const charCount = localContent.length
  const showCharCount = charCount > CHAR_COUNT_THRESHOLD

  return (
    <div
      data-testid={`box-item-${box.id}`}
      className={`rounded-lg border bg-gradient-to-br ${color.gradient} ${color.border} overflow-hidden`}
    >
      {/* Box Header */}
      <div className={`px-4 py-2 flex items-center justify-between ${color.text} bg-black/20`}>
        <div className="flex items-center gap-2">
          {/* FR-2.2: Numbered color badge with accessible label */}
          <span
            data-testid={`box-badge-${box.order}`}
            className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white ${color.badgeBg}`}
            aria-label={`Box ${box.order}, ${color.name}`}
          >
            {box.order}
          </span>
          <h3 className="font-semibold text-sm">Box {box.order}</h3>
        </div>
        {canRemove && <RemoveBoxButton onClick={handleRemove} disabled={false} />}
      </div>

      {/* Box Content */}
      <div className="p-4">
        <textarea
          value={localContent}
          onChange={handleContentChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          data-testid={`box-textarea-${box.id}`}
          className="w-full h-24 px-3 py-2 bg-gray-900/60 border border-gray-700
                     text-gray-200 placeholder-gray-500 rounded-md
                     focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent
                     resize-none text-sm"
          aria-label={`Content for box ${box.order}`}
        />
        {/* Character count - shown when exceeding threshold (FR-3.4) */}
        {showCharCount && (
          <div
            data-testid={`box-char-count-${box.id}`}
            className="mt-1 text-xs text-amber-400/80 text-right"
          >
            {charCount} characters (consider keeping it concise)
          </div>
        )}
      </div>
    </div>
  )
}
